import React, { useCallback, useMemo } from 'react';

import { styled } from '@this/constants/themes';
import A from '@this/src/components/shared/atoms/a';
import AutoCompletableInput from '@this/src/components/shared/autocompletable_input/autocompletable_input';
import { AddressArea, Field, FieldsWrapper, InLabel, InputArea } from '@this/src/components/shared/search/search';
import type ChatbotMessage from '@this/src/domain/chatbot/chatbot_message';
import type { OriginField } from '@this/src/domain/chatbot/chatbot_message';
import type User from '@this/src/domain/user/user';
import { useGeolocation } from '@this/src/lib/hooks/useGeoLocation';

interface Props extends OriginFieldOptions {
  message: ChatbotMessage;
}

export interface OriginFieldOptions {
  availableOptions?: string[];
  user?: User | null;
}

export const ChatMessageFormOrigin: React.FC<Props> = ({ message, availableOptions, user }) => {
  const { loading, error, getCurrentPlace } = useGeolocation();

  const formField = message.formFields.find(field => field.type === 'origin') as OriginField;
  const origin = formField?.value as string;
  const originAddress = formField?.address as string;
  const restrictionDistanceAndTimeAvailability = useMemo(
    () => availableOptions?.includes('restriction_distance_and_time') ?? false,
    [availableOptions]
  );

  const handlePlaceChange = useCallback(
    (_name: unknown, value: string, address: string) => {
      message.setFormFieldOrigin(value, address);
    },
    [message]
  );

  const handleGetCurrentPlace = useCallback(
    async (evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      try {
        const { formattedAddress } = await getCurrentPlace(evt);
        message.setFormFieldOrigin(formattedAddress);
      } catch (err) {
        console.error('現在地の取得に失敗しました:', err);
      }
    },
    [getCurrentPlace, message]
  );

  return (
    <FieldsWrapper className="search__block__body__fields-wrapper">
      <Field>
        <InLabel htmlFor="origin">出発地</InLabel>
        <FixedInputArea wrapped>
          <AutoCompletableInput onChange={handlePlaceChange} value={origin || ''} id="origin" />
          <div className="search__input-current-place">
            {loading ? (
              <div className="search-loader-line-mask">
                <SearchLoaderLine />
              </div>
            ) : (
              <CurrentPlace title="現在地を取得" onClick={handleGetCurrentPlace}>
                現在地
              </CurrentPlace>
            )}
          </div>
        </FixedInputArea>
        <AddressArea>
          <p>{originAddress}</p>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {restrictionDistanceAndTimeAvailability && user?.hasOrganizationBase() && (
            <p>{`拠点情報: ${user?.organizationBase.address}`}</p>
          )}
        </AddressArea>
      </Field>
    </FieldsWrapper>
  );
};

const SearchLoaderLine = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  box-shadow: inset 0 1px 1px 3px ${props => props.theme.linkColor};
`;

const CurrentPlace = styled(A)`
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
`;

const FixedInputArea = styled(InputArea)`
  position: relative;
  & .search__suggest-container {
    left: -16px;
    max-width: 278px;
  }
`;
